import React, { useState } from 'react';
import {
  Compass,
  Layers,
  Cpu,
  Database,
  Shield,
  Zap,
  Activity,
  AlertTriangle,
  CheckCircle,
  FileText,
  DollarSign,
  TrendingUp,
  Server,
  Share2,
  Users,
  Code2,
  Radio,
  BookOpen,
  FolderGit2,
  ArrowRight,
  ExternalLink,
  Search,
  Filter,
  Check,
  Clock,
  Flame,
  Award
} from 'lucide-react';
import { AppView } from '../../types';
import {
  MOCK_CATALOG_ENTITIES,
  MOCK_TEAMS,
  MOCK_APPLICATION_MODULES,
  MOCK_DEPENDENCIES,
  MOCK_GOLDEN_PATHS,
  MOCK_ADRS,
  MOCK_TECH_RADAR,
  MOCK_ENGINEERING_STANDARDS,
  MOCK_DORA_METRICS,
  MOCK_FINOPS_COSTS,
  MOCK_UNIT_ECONOMICS,
  MOCK_TECH_DEBT,
  MOCK_ARCHITECTURE_RISKS,
  MOCK_PLATFORM_HEALTH
} from '../../services/platform/platformStore';

interface ChiefTechnologyOfficerCommandCenterProps {
  onNavigateToView?: (view: AppView) => void;
}

export const ChiefTechnologyOfficerCommandCenter: React.FC<ChiefTechnologyOfficerCommandCenterProps> = ({
  onNavigateToView
}) => {
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'c4-architecture'
    | 'catalog-ownership'
    | 'dependencies'
    | 'devex-dora'
    | 'api-events'
    | 'adrs'
    | 'radar-sprawl'
    | 'standards-scorecards'
    | 'debt-risks'
    | 'finops-economics'
    | 'platform-health'
  >('overview');

  const [adrSearch, setAdrSearch] = useState('');
  const [selectedAdrStatus, setSelectedAdrStatus] = useState<string>('ALL');

  // Filter ADRs
  const filteredAdrs = MOCK_ADRS.filter((a) => {
    const matchesSearch =
      a.id.toLowerCase().includes(adrSearch.toLowerCase()) ||
      a.title.toLowerCase().includes(adrSearch.toLowerCase()) ||
      a.context.toLowerCase().includes(adrSearch.toLowerCase());
    const matchesStatus = selectedAdrStatus === 'ALL' || a.status === selectedAdrStatus;
    return matchesSearch && matchesStatus;
  });

  const totalTechSpend = MOCK_FINOPS_COSTS.reduce((acc, c) => acc + c.amount, 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-sans pb-24">
      {/* Top Banner */}
      <div className="bg-slate-900 text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-md">
                <Compass className="w-7 h-7" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-black tracking-tight text-white">Chief Technology Officer (CTO) OS</h1>
                  <span className="text-[10px] font-black bg-indigo-500/30 text-indigo-300 border border-indigo-500/40 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                    Prompt 26
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  Enterprise Architecture, Platform Engineering, IDP, DORA Metrics, FinOps & Technology Governance
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <button
                onClick={() => onNavigateToView?.('internal-developer-platform')}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm"
              >
                <Layers className="w-4 h-4" />
                <span>Open Developer Portal (IDP)</span>
              </button>
              <button
                onClick={() => onNavigateToView?.('production-command-center')}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-xl transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <Server className="w-4 h-4 text-emerald-400" />
                <span>Dokploy VPS Ops</span>
              </button>
            </div>
          </div>

          {/* Executive KPI Header Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-6 pt-6 border-t border-slate-800 text-xs">
            <div className="bg-slate-800/80 p-3 rounded-xl border border-slate-700/50">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Production Services</span>
              <div className="text-base font-black text-white mt-1">18 Components</div>
              <div className="text-[10px] text-emerald-400 font-semibold mt-0.5">100% Cataloged</div>
            </div>

            <div className="bg-slate-800/80 p-3 rounded-xl border border-slate-700/50">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Ownership Coverage</span>
              <div className="text-base font-black text-white mt-1">100.0%</div>
              <div className="text-[10px] text-emerald-400 font-semibold mt-0.5">0 Orphan Components</div>
            </div>

            <div className="bg-slate-800/80 p-3 rounded-xl border border-slate-700/50">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">DORA Software Delivery</span>
              <div className="text-base font-black text-indigo-300 mt-1">ELITE Tier</div>
              <div className="text-[10px] text-slate-300 font-semibold mt-0.5">{MOCK_DORA_METRICS.changeLeadTimeHours}h Lead Time</div>
            </div>

            <div className="bg-slate-800/80 p-3 rounded-xl border border-slate-700/50">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Change Fail Rate</span>
              <div className="text-base font-black text-emerald-400 mt-1">{MOCK_DORA_METRICS.changeFailRatePercentage}%</div>
              <div className="text-[10px] text-slate-400 font-semibold mt-0.5">MTTR: {MOCK_DORA_METRICS.failedDeploymentRecoveryTimeMinutes}m</div>
            </div>

            <div className="bg-slate-800/80 p-3 rounded-xl border border-slate-700/50">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Monthly Tech Spend</span>
              <div className="text-base font-black text-white mt-1">${totalTechSpend.toFixed(2)}/mo</div>
              <div className="text-[10px] text-emerald-400 font-semibold mt-0.5">Hostinger + AI + Feeds</div>
            </div>

            <div className="bg-slate-800/80 p-3 rounded-xl border border-slate-700/50">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Unit Calculation Cost</span>
              <div className="text-base font-black text-emerald-400 mt-1">$0.116 / 1k</div>
              <div className="text-[10px] text-slate-400 font-semibold mt-0.5">-8.4% MoM Efficiency</div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-2.5">
            {[
              { id: 'overview', label: 'Executive Overview', icon: Compass },
              { id: 'c4-architecture', label: 'C4 Enterprise Architecture', icon: Layers },
              { id: 'catalog-ownership', label: 'Catalog & Ownership', icon: Users },
              { id: 'dependencies', label: 'Dependencies & Blast Radius', icon: Share2 },
              { id: 'devex-dora', label: 'DevEx & DORA Metrics', icon: TrendingUp },
              { id: 'api-events', label: 'API & Event Governance', icon: Code2 },
              { id: 'adrs', label: 'Architecture Decisions (ADRs)', icon: FileText },
              { id: 'radar-sprawl', label: 'Tech Radar & Sprawl', icon: Radio },
              { id: 'standards-scorecards', label: 'Standards & Scorecards', icon: Shield },
              { id: 'debt-risks', label: 'Tech Debt & Risks', icon: Flame },
              { id: 'finops-economics', label: 'FinOps & Unit Economics', icon: DollarSign },
              { id: 'platform-health', label: 'Platform Health & Ops', icon: Activity }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-3 py-2 text-xs font-bold rounded-lg flex items-center gap-2 whitespace-nowrap transition-all cursor-pointer ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content View */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* TAB 1: EXECUTIVE OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Mission Statement Box */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-black text-slate-900">
                    YamooPay Technology Operating System
                  </h2>
                  <p className="text-xs text-slate-600 mt-1 max-w-3xl leading-relaxed">
                    We maintain an uncompromising balance between ultra-fast product iteration and financial-grade precision. 
                    Our architecture strictly adheres to a <strong>Modular Monolith First</strong> pattern on Hostinger VPS NVMe containers, 
                    preventing premature microservices sprawl and unnecessary distributed system failure modes.
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-[10px] font-mono font-bold bg-slate-100 text-slate-700 px-2 py-1 rounded">
                    Hostinger VPS KVM8 • Dokploy PaaS
                  </span>
                </div>
              </div>

              {/* Quick Architectural Invariants */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-100 text-xs">
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                  <span className="font-bold text-slate-900 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Modular Monolith First</span>
                  </span>
                  <p className="text-slate-600 text-[11px] leading-relaxed">
                    Zero network serialization overhead for internal gross-to-net salary and mortgage calculations. In-process isolation via strict exports.
                  </p>
                </div>

                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                  <span className="font-bold text-slate-900 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Deterministic Financial Core</span>
                  </span>
                  <p className="text-slate-600 text-[11px] leading-relaxed">
                    All calculations use fixed-point arithmetic or scaled integers. Zero floating-point rounding artifacts across all 25+ tax jurisdictions.
                  </p>
                </div>

                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                  <span className="font-bold text-slate-900 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Transparent FinOps ($484/mo)</span>
                  </span>
                  <p className="text-slate-600 text-[11px] leading-relaxed">
                    Hostinger VPS + Gemini AI token efficiency allows us to operate global financial intelligence at $0.116 per 1,000 calculations.
                  </p>
                </div>
              </div>
            </div>

            {/* Core Systems Status Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Monolith Modules */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-2xs space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-indigo-600" />
                    <h3 className="text-sm font-bold text-slate-900">Application Domain Modules</h3>
                  </div>
                  <span className="text-xs font-mono text-slate-400">{MOCK_APPLICATION_MODULES.length} bounded domains</span>
                </div>

                <div className="space-y-3">
                  {MOCK_APPLICATION_MODULES.map((mod) => (
                    <div key={mod.moduleId} className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                      <div className="flex items-center justify-between font-bold text-slate-900">
                        <span>{mod.name}</span>
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                          {mod.isolationScore}% Isolated
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 font-mono">{mod.domain} • Tables: {mod.databaseTablesOwned.join(', ')}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* DORA 5-Key Metrics Dashboard */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-2xs space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                    <h3 className="text-sm font-bold text-slate-900">DORA Software Delivery Performance</h3>
                  </div>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                    {MOCK_DORA_METRICS.overallDoraTier} TIER
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold uppercase">Change Lead Time</span>
                    <div className="text-lg font-black text-slate-900 mt-0.5">{MOCK_DORA_METRICS.changeLeadTimeHours} hours</div>
                    <div className="text-[10px] text-emerald-700 font-semibold mt-0.5">Commit to Production</div>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold uppercase">Deployment Frequency</span>
                    <div className="text-lg font-black text-slate-900 mt-0.5">{MOCK_DORA_METRICS.deploymentFrequencyPerDay} / day</div>
                    <div className="text-[10px] text-emerald-700 font-semibold mt-0.5">Automated CI Rollouts</div>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold uppercase">Recovery Time (MTTR)</span>
                    <div className="text-lg font-black text-slate-900 mt-0.5">{MOCK_DORA_METRICS.failedDeploymentRecoveryTimeMinutes} min</div>
                    <div className="text-[10px] text-emerald-700 font-semibold mt-0.5">Dokploy 1-Click Rollback</div>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 font-bold uppercase">Change Fail Rate</span>
                    <div className="text-lg font-black text-emerald-700 mt-0.5">{MOCK_DORA_METRICS.changeFailRatePercentage}%</div>
                    <div className="text-[10px] text-slate-500 font-semibold mt-0.5">Defect Rework: {MOCK_DORA_METRICS.deploymentReworkRatePercentage}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: C4 ENTERPRISE ARCHITECTURE */}
        {activeTab === 'c4-architecture' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <h2 className="text-base font-black text-slate-900">YamooPay C4 Model & Capability Architecture</h2>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Clear visual boundaries: System Context, Deployable Containers on Hostinger VPS, and In-Process Bounded Modules.
              </p>

              {/* C4 Diagrammatic Representation */}
              <div className="mt-6 p-6 bg-slate-900 text-white rounded-2xl border border-slate-800 space-y-6">
                <div className="text-center pb-4 border-b border-slate-800">
                  <span className="text-[10px] font-mono font-bold text-indigo-400 uppercase tracking-wider">Level 1: System Context & External Actors</span>
                  <div className="flex justify-center items-center gap-4 mt-3 text-xs">
                    <div className="bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700">Consumers (Web / PWA)</div>
                    <div className="text-slate-500 font-mono">→ [TLS / CDN] →</div>
                    <div className="bg-indigo-600 px-4 py-2 rounded-xl font-bold border border-indigo-400">YamooPay Global Platform</div>
                    <div className="text-slate-500 font-mono">→ [REST / Webhooks] →</div>
                    <div className="bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700">Central Banks / Stripe</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                  <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700">
                    <span className="text-[10px] font-bold text-emerald-400 uppercase">Container A: Frontend Web & PWA</span>
                    <div className="font-bold text-white mt-1">React 18 + Vite SPA</div>
                    <p className="text-[11px] text-slate-400 mt-1">Zero latency client-side evaluation + Offline LocalStorage state cache.</p>
                  </div>

                  <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700">
                    <span className="text-[10px] font-bold text-indigo-400 uppercase">Container B: Core Application Server</span>
                    <div className="font-bold text-white mt-1">Express & Node v22 LTS</div>
                    <p className="text-[11px] text-slate-400 mt-1">Acts as secure API proxy, JWT validation gate, and server-side calculation engine.</p>
                  </div>

                  <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700">
                    <span className="text-[10px] font-bold text-purple-400 uppercase">Container C: System of Record</span>
                    <div className="font-bold text-white mt-1">PostgreSQL 17 on NVMe</div>
                    <p className="text-[11px] text-slate-400 mt-1">ACID transactional integrity, pgvector RAG embeddings, and WAL backup archives.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: CATALOG & OWNERSHIP */}
        {activeTab === 'catalog-ownership' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div>
                  <h2 className="text-base font-black text-slate-900">Engineering Teams & Ownership Registry</h2>
                  <p className="text-xs text-slate-600 mt-0.5">Every production component has an assigned engineering team and on-call rotation.</p>
                </div>
                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                  100% Owned (0 Orphans)
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {MOCK_TEAMS.map((team) => (
                  <div key={team.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-slate-900 text-sm">{team.name}</h4>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${team.busFactorScore <= 2 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                        Bus Factor: {team.busFactorScore}
                      </span>
                    </div>
                    <div className="text-slate-600">Lead: <strong>{team.lead}</strong> ({team.membersCount} members)</div>
                    <div className="text-[11px] text-slate-500 font-mono flex items-center justify-between pt-2 border-t border-slate-200">
                      <span>{team.slackChannel}</span>
                      <span>{team.onCallRotation}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: DEPENDENCIES & BLAST RADIUS */}
        {activeTab === 'dependencies' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <h2 className="text-base font-black text-slate-900">Dependency Graph & Single Points of Failure</h2>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Tracking declared and observed telemetry calls between modules, databases, and third-party APIs.
              </p>

              <div className="divide-y divide-slate-100 mt-6 text-xs">
                {MOCK_DEPENDENCIES.map((dep) => (
                  <div key={dep.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-slate-900">{dep.sourceName}</span>
                      <span className="text-[10px] font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-600 font-bold">
                        {dep.type}
                      </span>
                      <span className="font-bold text-indigo-700">{dep.targetName}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {dep.isSinglePointOfFailure && (
                        <span className="text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                          SPOF Risk
                        </span>
                      )}
                      <span className="text-[10px] font-mono text-slate-400">Observed {dep.lastObservedAt}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: DEVEX & DORA METRICS */}
        {activeTab === 'devex-dora' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs space-y-6">
              <h2 className="text-base font-black text-slate-900">DORA Software Delivery Metrics & Trend</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {MOCK_DORA_METRICS.historicalTrend.map((t) => (
                  <div key={t.week} className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-1">
                    <span className="font-bold text-slate-500">{t.week} Performance</span>
                    <div className="text-base font-black text-slate-900">{t.deployments} Production Deploys</div>
                    <div className="text-[11px] text-slate-600">Lead Time: {t.leadTimeHours}h • Fail: {t.failRatePct}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 6: API & EVENT GOVERNANCE */}
        {activeTab === 'api-events' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <h2 className="text-base font-black text-slate-900">API & Domain Event Contracts (OpenAPI & AsyncAPI)</h2>
              <p className="text-xs text-slate-600 mt-1">Versioned contracts, breaking-change guards, and RFC 7807 problem details error models.</p>

              <div className="divide-y divide-slate-100 mt-6 text-xs">
                <div className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-slate-900">/v1/salary/calculate</div>
                    <div className="text-[11px] text-slate-500 font-mono">Public REST • OpenAPI 3.1 • Bearer JWT / API Key • 120k calls/mo</div>
                  </div>
                  <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">STABLE</span>
                </div>

                <div className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-slate-900">/v1/rates/live</div>
                    <div className="text-[11px] text-slate-500 font-mono">Public REST • OpenAPI 3.1 • API Key • 450k calls/mo</div>
                  </div>
                  <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">STABLE</span>
                </div>

                <div className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-slate-900">event: billing.payment_succeeded</div>
                    <div className="text-[11px] text-slate-500 font-mono">AsyncAPI 3.0 Domain Event • At-Least-Once • Producer: Billing</div>
                  </div>
                  <span className="text-[10px] font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">ACTIVE</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 7: ARCHITECTURE DECISION RECORDS (ADRs) */}
        {activeTab === 'adrs' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-100">
                <div>
                  <h2 className="text-base font-black text-slate-900">Architecture Decision Records (ADRs)</h2>
                  <p className="text-xs text-slate-600 mt-0.5">Immutable record of strategic architecture choices and tradeoffs.</p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search ADRs..."
                      value={adrSearch}
                      onChange={(e) => setAdrSearch(e.target.value)}
                      className="pl-8 pr-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg"
                    />
                  </div>

                  <select
                    value={selectedAdrStatus}
                    onChange={(e) => setSelectedAdrStatus(e.target.value)}
                    className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 font-medium"
                  >
                    <option value="ALL">All Status</option>
                    <option value="ACCEPTED">Accepted</option>
                    <option value="PROPOSED">Proposed</option>
                    <option value="SUPERSEDED">Superseded</option>
                  </select>
                </div>
              </div>

              <div className="divide-y divide-slate-100 mt-4 text-xs">
                {filteredAdrs.map((adr) => (
                  <div key={adr.id} className="py-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                          {adr.id}
                        </span>
                        <h4 className="font-bold text-slate-900 text-sm">{adr.title}</h4>
                      </div>
                      <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                        {adr.status}
                      </span>
                    </div>
                    <p className="text-slate-600 leading-relaxed text-xs">{adr.decision}</p>
                    <div className="text-[11px] text-slate-500 flex items-center justify-between pt-1">
                      <span>Author: {adr.author} ({adr.date})</span>
                      <span className="font-mono">Reviewers: {adr.reviewers.join(', ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 8: TECH RADAR & SPRAWL */}
        {activeTab === 'radar-sprawl' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <h2 className="text-base font-black text-slate-900">Technology Sprawl & Tooling Guardrails</h2>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Strict limits on tool diversity to prevent operational fragmentation and maintain engineering depth.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 text-xs">
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <span className="font-bold text-slate-900 block">Single Datastore Policy</span>
                  <p className="text-slate-600 text-[11px]">PostgreSQL 17 is the primary system of record. No MongoDB, DynamoDB, or Cassandra permitted without formal CTO ADR approval.</p>
                </div>

                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <span className="font-bold text-slate-900 block">Universal Language: TypeScript</span>
                  <p className="text-slate-600 text-[11px]">100% of frontend, backend, workers, and calculators written in strict TypeScript. Zero Python/Go fragmentation.</p>
                </div>

                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <span className="font-bold text-slate-900 block">Single Deployment Target: Dokploy</span>
                  <p className="text-slate-600 text-[11px]">Docker Compose on Hostinger VPS. Kubernetes is explicitly placed on HOLD to avoid infrastructure overhead.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 9: STANDARDS & SCORECARDS */}
        {activeTab === 'standards-scorecards' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <h2 className="text-base font-black text-slate-900">Engineering Scorecards & Quality Gates</h2>
              <div className="divide-y divide-slate-100 mt-6 text-xs">
                {MOCK_CATALOG_ENTITIES.map((entity) => (
                  <div key={entity.entityId} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-bold text-slate-900">{entity.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{entity.ownership.ownerTeamName} • SLO {entity.slo.availabilityTarget}</div>
                    </div>
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" /> 100% Scorecard Pass
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 10: TECH DEBT & ARCHITECTURE RISKS */}
        {activeTab === 'debt-risks' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Architecture Risks */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs space-y-4">
                <h3 className="text-sm font-bold text-slate-900">Architecture Risk Register</h3>
                <div className="space-y-3 text-xs">
                  {MOCK_ARCHITECTURE_RISKS.map((risk) => (
                    <div key={risk.id} className="p-3.5 bg-amber-50/50 border border-amber-200 rounded-xl space-y-1.5">
                      <div className="flex items-center justify-between font-bold text-slate-900">
                        <span>{risk.title}</span>
                        <span className="text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          {risk.status}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-600">{risk.threatScenario}</p>
                      <div className="text-[10px] text-indigo-900 font-medium pt-1">
                        <strong>Mitigation:</strong> {risk.mitigationPlan}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Technical Debt */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs space-y-4">
                <h3 className="text-sm font-bold text-slate-900">Technical Debt Registry</h3>
                <div className="space-y-3 text-xs">
                  {MOCK_TECH_DEBT.map((debt) => (
                    <div key={debt.id} className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5">
                      <div className="flex items-center justify-between font-bold text-slate-900">
                        <span>{debt.title}</span>
                        <span className="text-[10px] font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                          {debt.status}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-600">{debt.description}</p>
                      <div className="text-[10px] text-slate-500 font-mono">
                        Target remediation: {debt.targetRemediationDate} • Owner: {debt.ownerTeam}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 11: FINOPS & UNIT ECONOMICS */}
        {activeTab === 'finops-economics' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div>
                  <h2 className="text-base font-black text-slate-900">Technology FinOps & Cost Breakdown</h2>
                  <p className="text-xs text-slate-600 mt-0.5">Total technology spend: ${totalTechSpend.toFixed(2)} / month.</p>
                </div>
                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                  $0.116 per 1,000 Calculations
                </span>
              </div>

              {/* Invoices Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                {MOCK_FINOPS_COSTS.map((cost) => (
                  <div key={cost.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">{cost.category}</span>
                      <span className="font-bold text-slate-900 font-mono">${cost.amount.toFixed(2)}/mo</span>
                    </div>
                    <div className="font-bold text-slate-900 text-sm">{cost.provider}</div>
                    <p className="text-[11px] text-slate-600">{cost.service}</p>
                  </div>
                ))}
              </div>

              {/* Unit Economics Table */}
              <div className="pt-4 border-t border-slate-100">
                <h3 className="text-xs font-bold text-slate-500 uppercase mb-3">Unit Economics Metrics</h3>
                <div className="divide-y divide-slate-100 text-xs">
                  {MOCK_UNIT_ECONOMICS.map((u) => (
                    <div key={u.metricName} className="py-3 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-slate-900">{u.metricName}</div>
                        <div className="text-[11px] text-slate-500">{u.unitDefinition}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold text-slate-900">${u.costPerUnitUsd.toFixed(6)}</div>
                        <div className="text-[10px] text-emerald-700 font-semibold">{u.trendPercentage}% MoM</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 12: PLATFORM HEALTH & OPS */}
        {activeTab === 'platform-health' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-2xs">
              <h2 className="text-base font-black text-slate-900">Platform Control Plane & Infrastructure Status</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 text-xs">
                <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <span className="text-[10px] text-emerald-800 font-bold uppercase">Software Catalog</span>
                  <div className="text-sm font-black text-emerald-900 mt-1">HEALTHY</div>
                </div>
                <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <span className="text-[10px] text-emerald-800 font-bold uppercase">Dokploy Integration</span>
                  <div className="text-sm font-black text-emerald-900 mt-1">HEALTHY</div>
                </div>
                <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <span className="text-[10px] text-emerald-800 font-bold uppercase">CI/CD Pipeline</span>
                  <div className="text-sm font-black text-emerald-900 mt-1">HEALTHY</div>
                </div>
                <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <span className="text-[10px] text-emerald-800 font-bold uppercase">OTel Telemetry</span>
                  <div className="text-sm font-black text-emerald-900 mt-1">HEALTHY</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
